import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../services/supabase';
import { MODULES } from '../../config/moduleDefs';
import { Card, Button, Badge, parseDateInput } from '../ui/UIComponents';
import { Users, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, Gauge } from 'lucide-react';

const CAPACITY_STORAGE_KEY = 'staffhub.staff-capacity-targets.v1';
const DEFAULT_CAPACITY = 25;

const StaffWorkloadDashboard = ({ onNavigate, staffList = [] }) => {
  const [loading, setLoading] = useState(true);
  const [rawRows, setRawRows] = useState([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [unassignedByModule, setUnassignedByModule] = useState([]);
  const [capacityByStaff, setCapacityByStaff] = useState({});
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [sortKey, setSortKey] = useState('pressureScore');
  const [sortDirection, setSortDirection] = useState('desc');
  const [activeTopStat, setActiveTopStat] = useState('all');
  const unassignedSectionRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CAPACITY_STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : {};
      setCapacityByStaff(parsed && typeof parsed === 'object' ? parsed : {});
    } catch {
      setCapacityByStaff({});
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CAPACITY_STORAGE_KEY, JSON.stringify(capacityByStaff || {}));
  }, [capacityByStaff]);

  useEffect(() => {
    fetchWorkload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList]);

  const fetchWorkload = async () => {
    setLoading(true);

    const staffNameById = new Map((staffList || []).map((staff) => [String(staff.id), staff.name || staff.full_name || staff.email || `Staff ${staff.id}`]));

    const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

    const today = startOfDay(new Date());

    const getFridayEnd = (date) => {
      const day = date.getDay();
      const delta = (5 - day + 7) % 7;
      const friday = new Date(date);
      friday.setDate(date.getDate() + delta);
      return endOfDay(friday);
    };

    const weekEndFriday = getFridayEnd(today);
    const thisMonthEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const nextMonthEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 2, 0));
    const in2MonthsStart = new Date(today.getFullYear(), today.getMonth() + 2, 1);
    const in2MonthsEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 3, 0));

    const rowMap = new Map();
    const unassignedModuleMap = new Map();
    let nextUnassignedTotal = 0;

    const ensureRow = (staffId) => {
      const key = String(staffId);
      if (!rowMap.has(key)) {
        rowMap.set(key, {
          staffId: key,
          staffName: staffNameById.get(key) || `Staff ${key}`,
          totalAssigned: 0,
          overdue: 0,
          thisWeek: 0,
          thisMonth: 0,
          nextMonth: 0,
          in2Months: 0,
          noDueDate: 0,
          moduleMap: new Map(),
        });
      }
      return rowMap.get(key);
    };

    const upsertModuleBreakdown = (row, module, workstream) => {
      const existing = row.moduleMap.get(module.id) || {
        moduleId: module.id,
        moduleLabel: module.label,
        total: 0,
        workstreams: new Map(),
      };
      existing.total += 1;
      const wsLabel = workstream.shortLabel || workstream.label || workstream.key;
      existing.workstreams.set(workstream.key, {
        key: workstream.key,
        label: wsLabel,
        count: (existing.workstreams.get(workstream.key)?.count || 0) + 1,
      });
      row.moduleMap.set(module.id, existing);
    };

    try {
      for (const module of Object.values(MODULES)) {
        if (!module?.tableName || module.id === 'dashboard' || module.id === 'staff_workload') continue;

        const { data, error } = await supabase
          .from(module.tableName)
          .select('*');

        if (error) {
          console.error(`Error fetching ${module.tableName}:`, error);
          continue;
        }

        const scopedData = typeof module.postFetchFilter === 'function'
          ? data.filter(record => {
              try {
                return module.postFetchFilter(record);
              } catch {
                return true;
              }
            })
          : data;

        for (const workstream of module.workstreams || []) {
          if (!workstream?.staffField) continue;

          for (const client of scopedData) {
            const assignedStaffId = client[workstream.staffField];
            const dueDate = parseDateInput(workstream.dueField ? client[workstream.dueField] : null);
            const hasDueDate = Boolean(dueDate);

            if (!assignedStaffId) {
              nextUnassignedTotal += 1;
              const unassignedEntry = unassignedModuleMap.get(module.id) || { moduleId: module.id, moduleLabel: module.label, count: 0 };
              unassignedEntry.count += 1;
              unassignedModuleMap.set(module.id, unassignedEntry);
              continue;
            }

            const row = ensureRow(assignedStaffId);
            row.totalAssigned += 1;
            upsertModuleBreakdown(row, module, workstream);

            if (!hasDueDate) {
              row.noDueDate += 1;
              continue;
            }

            const due = endOfDay(dueDate);
            if (due < today) row.overdue += 1;
            if (due >= today && due <= weekEndFriday) row.thisWeek += 1;
            if (due >= today && due <= thisMonthEnd) row.thisMonth += 1;
            if (due >= nextMonthStart && due <= nextMonthEnd) row.nextMonth += 1;
            if (due >= in2MonthsStart && due <= in2MonthsEnd) row.in2Months += 1;
          }
        }
      }

      setRawRows(
        Array.from(rowMap.values()).map((row) => ({
          ...row,
          moduleBreakdown: Array.from(row.moduleMap.values())
            .map((entry) => ({
              ...entry,
              workstreamBreakdown: Array.from(entry.workstreams.values()).sort((a, b) => b.count - a.count),
            }))
            .sort((a, b) => b.total - a.total),
        }))
      );

      setUnassignedTotal(nextUnassignedTotal);
      setUnassignedByModule(Array.from(unassignedModuleMap.values()).sort((a, b) => b.count - a.count));
    } catch (error) {
      console.error('Error building staff workload dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => {
    const withDerived = rawRows.map((row) => {
      const capacity = Math.max(0, Number(capacityByStaff[row.staffId] ?? DEFAULT_CAPACITY));
      const variance = row.totalAssigned - capacity;
      const pressureScore = (row.overdue * 5) + (row.thisWeek * 3) + Math.max(variance, 0);
      return {
        ...row,
        capacity,
        variance,
        pressureScore,
      };
    });

    const direction = sortDirection === 'asc' ? 1 : -1;
    return withDerived.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av).localeCompare(String(bv)) * direction;
      }
      return (av - bv) * direction;
    });
  }, [rawRows, capacityByStaff, sortDirection, sortKey]);

  const totalAssigned = rows.reduce((sum, row) => sum + row.totalAssigned, 0);
  const totalOverdue = rows.reduce((sum, row) => sum + row.overdue, 0);
  const overloadedCount = rows.filter((row) => row.variance > 0).length;

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('desc');
  };

  const updateCapacity = (staffId, value) => {
    const next = Number(value);
    setCapacityByStaff((prev) => ({
      ...prev,
      [String(staffId)]: Number.isNaN(next) ? DEFAULT_CAPACITY : Math.max(0, next),
    }));
  };

  const openModule = (moduleId, staffId, staffName, filter = 'all') => {
    if (!moduleId || !onNavigate) return;
    onNavigate({ moduleId, filter, source: 'staff-workload', staffId, staffName });
  };

  const displayRows = useMemo(() => {
    if (activeTopStat === 'overdue') return rows.filter(row => row.overdue > 0);
    if (activeTopStat === 'overCapacity') return rows.filter(row => row.variance > 0);
    if (activeTopStat === 'assigned') return rows.filter(row => row.totalAssigned > 0);
    return rows;
  }, [rows, activeTopStat]);

  const activeSelectedStaff = displayRows.find((row) => row.staffId === selectedStaffId) || null;

  const handleTopStatClick = (statKey) => {
    if (activeTopStat === statKey) {
      setActiveTopStat('all');
      return;
    }

    setActiveTopStat(statKey);

    if (statKey === 'overdue') {
      setSortKey('overdue');
      setSortDirection('desc');
      return;
    }

    if (statKey === 'overCapacity') {
      setSortKey('variance');
      setSortDirection('desc');
      return;
    }

    if (statKey === 'assigned') {
      setSortKey('totalAssigned');
      setSortDirection('desc');
      return;
    }

    if (statKey === 'unassigned') {
      setSelectedStaffId(null);
      setTimeout(() => {
        unassignedSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    }
  };

  const SortableHeader = ({ field, label }) => {
    const active = sortKey === field;
    return (
      <button
        type="button"
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ${active ? 'text-white' : 'text-blue-100 hover:text-white'}`}
      >
        <span>{label}</span>
        {active ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3" />}
      </button>
    );
  };

  const staffRowToneClasses = [
    'bg-blue-50 border-blue-200',
    'bg-emerald-50 border-emerald-200',
    'bg-amber-50 border-amber-200',
    'bg-violet-50 border-violet-200',
    'bg-cyan-50 border-cyan-200',
    'bg-rose-50 border-rose-200',
    'bg-indigo-50 border-indigo-200',
  ];

  const workTypeRowToneClasses = [
    'bg-indigo-50 border-indigo-200',
    'bg-purple-50 border-purple-200',
    'bg-cyan-50 border-cyan-200',
    'bg-emerald-50 border-emerald-200',
    'bg-amber-50 border-amber-200',
    'bg-rose-50 border-rose-200',
  ];

  return (
    <div className="px-6 pb-6 pt-2 space-y-4">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <Button onClick={fetchWorkload} variant="outline" size="sm">Refresh</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={`p-0 border-2 transition-all ${activeTopStat === 'assigned' ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg' : 'border-slate-200 hover:border-blue-300'}`}>
          <button type="button" onClick={() => handleTopStatClick('assigned')} className="w-full p-4 text-left">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">Assigned Work Items</div>
                <div className="text-2xl font-bold text-slate-800">{totalAssigned}</div>
              </div>
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </button>
        </Card>

        <Card className={`p-0 border-2 bg-rose-50 transition-all ${activeTopStat === 'overdue' ? 'border-rose-500 ring-2 ring-rose-200 shadow-lg' : 'border-rose-200 hover:border-rose-400'}`}>
          <button type="button" onClick={() => handleTopStatClick('overdue')} className="w-full p-4 text-left">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-rose-700">Overdue Assigned</div>
                <div className="text-2xl font-bold text-rose-700">{totalOverdue}</div>
              </div>
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </div>
          </button>
        </Card>

        <Card className={`p-0 border-2 bg-amber-50 transition-all ${activeTopStat === 'unassigned' ? 'border-amber-500 ring-2 ring-amber-200 shadow-lg' : 'border-amber-200 hover:border-amber-400'}`}>
          <button type="button" onClick={() => handleTopStatClick('unassigned')} className="w-full p-4 text-left">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-amber-700">Unassigned Queue</div>
                <div className="text-2xl font-bold text-amber-700">{unassignedTotal}</div>
              </div>
              <Badge variant={unassignedTotal > 0 ? 'warning' : 'success'}>{unassignedTotal > 0 ? 'Needs assignment' : 'Clear'}</Badge>
            </div>
          </button>
        </Card>

        <Card className={`p-0 border-2 bg-violet-50 transition-all ${activeTopStat === 'overCapacity' ? 'border-violet-500 ring-2 ring-violet-200 shadow-lg' : 'border-violet-200 hover:border-violet-400'}`}>
          <button type="button" onClick={() => handleTopStatClick('overCapacity')} className="w-full p-4 text-left">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-violet-700">Over Capacity</div>
                <div className="text-2xl font-bold text-violet-700">{overloadedCount}</div>
              </div>
              <Gauge className="w-6 h-6 text-violet-600" />
            </div>
          </button>
        </Card>
      </div>

      {activeTopStat !== 'all' && (
        <div className="flex items-center justify-between rounded-lg border border-blue-300 bg-blue-50 px-3 py-2">
          <div className="text-xs font-semibold text-blue-700">
            Active focus: {activeTopStat === 'assigned' ? 'Assigned Work Items' : activeTopStat === 'overdue' ? 'Overdue Assigned' : activeTopStat === 'unassigned' ? 'Unassigned Queue' : 'Over Capacity'}
          </div>
          <Button variant="outline" size="sm" onClick={() => setActiveTopStat('all')}>Clear Focus</Button>
        </div>
      )}

      <Card className="p-4 border-2 border-blue-500 bg-blue-100 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Staff Workload Board</h2>

        {loading ? (
          <div className="text-sm text-slate-500">Loading staff workloads...</div>
        ) : displayRows.length === 0 ? (
          <div className="text-sm text-slate-500">No staff assignments found yet.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border-2 border-blue-300 bg-white/90 shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b-2 border-blue-300 bg-gradient-to-r from-blue-700 via-indigo-700 to-violet-700">
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="staffName" label="Staff" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="totalAssigned" label="Assigned" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="overdue" label="Overdue" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="thisWeek" label="This Week" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="thisMonth" label="This Month" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="nextMonth" label="Next Month" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="in2Months" label="In 2 Months" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="noDueDate" label="No Due" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="capacity" label="Capacity" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="variance" label="Variance" /></th>
                  <th className="py-2.5 px-2 text-left"><SortableHeader field="pressureScore" label="Pressure" /></th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, rowIndex) => {
                  const isActive = selectedStaffId === row.staffId;
                  const rowTone = staffRowToneClasses[rowIndex % staffRowToneClasses.length];
                  return (
                    <tr key={row.staffId} className={`border-b border-white/80 cursor-pointer transition-colors ${isActive ? 'bg-blue-200 ring-1 ring-blue-400' : `${rowTone} hover:bg-blue-100`}`} onClick={() => setSelectedStaffId(row.staffId)}>
                      <td className="py-2.5 px-2 pr-3 font-semibold text-slate-800">{row.staffName}</td>
                      <td className="py-2.5 px-2 pr-3 font-medium text-slate-700">{row.totalAssigned}</td>
                      <td className="py-2.5 px-2 pr-3 text-rose-600 font-semibold">{row.overdue}</td>
                      <td className="py-2.5 px-2 pr-3 text-amber-700 font-semibold">{row.thisWeek}</td>
                      <td className="py-2.5 px-2 pr-3 text-slate-700">{row.thisMonth}</td>
                      <td className="py-2.5 px-2 pr-3 text-slate-700">{row.nextMonth}</td>
                      <td className="py-2.5 px-2 pr-3 text-slate-700">{row.in2Months}</td>
                      <td className="py-2.5 px-2 pr-3 text-slate-700">{row.noDueDate}</td>
                      <td className="py-2.5 px-2 pr-3" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="number"
                          min="0"
                          value={row.capacity}
                          onChange={(event) => updateCapacity(row.staffId, event.target.value)}
                          className="w-16 rounded border border-slate-400 bg-white px-2 py-1 text-xs font-semibold"
                        />
                      </td>
                      <td className={`py-2.5 px-2 pr-3 font-semibold ${row.variance > 0 ? 'text-rose-600' : row.variance < 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                        {row.variance > 0 ? `+${row.variance}` : row.variance}
                      </td>
                      <td className="py-2.5 px-2 pr-3">
                        <Badge variant={row.pressureScore >= 15 ? 'danger' : row.pressureScore >= 8 ? 'warning' : 'success'}>{row.pressureScore}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-6">
        <Card className="p-4 border-2 border-violet-500 bg-violet-100 shadow-xl">
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Staff Drilldown</h2>
          <p className="text-xs text-slate-500 mb-3">Select a staff row to inspect module/workstream load and launch focused views.</p>

          {unassignedByModule.length > 0 && (
            <div ref={unassignedSectionRef} className={`mb-3 rounded-lg border px-3 py-2 transition-all ${activeTopStat === 'unassigned' ? 'border-amber-400 bg-amber-100 ring-2 ring-amber-200' : 'border-amber-200 bg-amber-50'}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">Unassigned by module</div>
              <div className="flex flex-wrap gap-1.5">
                {unassignedByModule.map((entry) => (
                  <button
                    key={`unassigned-${entry.moduleId}`}
                    type="button"
                    onClick={() => openModule(entry.moduleId, 'unassigned', 'Unassigned', 'all')}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100"
                  >
                    <span>{entry.moduleLabel}</span>
                    <span>{entry.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!activeSelectedStaff ? (
            <div className="text-sm text-slate-500">Select a staff member from the table to open their workload drilldown.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-base font-semibold text-slate-800">{activeSelectedStaff.staffName}</div>
                  <div className="text-xs text-slate-500">{activeSelectedStaff.totalAssigned} assigned • {activeSelectedStaff.overdue} overdue</div>
                </div>
                <Badge variant={activeSelectedStaff.variance > 0 ? 'danger' : activeSelectedStaff.variance < 0 ? 'success' : 'secondary'}>
                  {activeSelectedStaff.variance > 0 ? `+${activeSelectedStaff.variance}` : activeSelectedStaff.variance} variance
                </Badge>
              </div>

              <div className="space-y-2">
                {activeSelectedStaff.moduleBreakdown.map((module, moduleIndex) => {
                  const workTypeTone = workTypeRowToneClasses[moduleIndex % workTypeRowToneClasses.length];
                  return (
                  <div key={`drill-${activeSelectedStaff.staffId}-${module.moduleId}`} className={`rounded-lg border-2 px-3 py-2 shadow-sm ${workTypeTone}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-700">{module.moduleLabel}</div>
                      <Badge variant="secondary">{module.total}</Badge>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {module.workstreamBreakdown.map((ws) => (
                        <span key={`${module.moduleId}-${ws.key}`} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white/95 px-2 py-0.5 text-[11px] text-slate-700 font-medium">
                          <span>{ws.label}</span>
                          <span className="font-semibold">{ws.count}</span>
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openModule(module.moduleId, activeSelectedStaff.staffId, activeSelectedStaff.staffName, 'all')}
                      >
                        Open {module.moduleLabel}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openModule(module.moduleId, 'unassigned', 'Unassigned', 'all')}
                      >
                        Open Unassigned
                      </Button>
                    </div>
                  </div>
                )})}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StaffWorkloadDashboard;
